import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { PenLine, Sparkles, Loader2, Check, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BlogStudio({ onPublished, presetTopic }) {
    const { user } = useAuth();
    const { language } = useLanguage();
    const de = language === 'de';
    const [drafts, setDrafts] = useState([]);
    const [topic, setTopic] = useState('');
    const [generating, setGenerating] = useState(false);
    const [busySlug, setBusySlug] = useState(null);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => { if (presetTopic) setTopic(presetTopic); }, [presetTopic]);

    const loadDrafts = useCallback(() => {
        axios.get(`${API}/blog/studio/drafts`)
            .then(res => setDrafts(res.data.drafts || []))
            .catch(() => setDrafts([]));
    }, []);

    useEffect(() => { if (user) loadDrafts(); }, [user, loadDrafts]);

    if (!user) return null;

    const generate = async () => {
        setGenerating(true);
        try {
            await axios.post(`${API}/blog/studio/generate`, { topic: topic.trim() || null });
            setTopic('');
            toast.success(de ? 'Entwurf erstellt – bitte prüfen! 📝' : 'Draft created – please review! 📝');
            loadDrafts();
        } catch (e) {
            toast.error(e?.response?.data?.detail || (de ? 'Generierung fehlgeschlagen' : 'Generation failed'));
        } finally {
            setGenerating(false);
        }
    };

    const publish = async (slug) => {
        setBusySlug(slug);
        try {
            await axios.post(`${API}/blog/studio/drafts/${slug}/publish`);
            toast.success(de ? 'Artikel veröffentlicht! 🎉' : 'Article published! 🎉');
            loadDrafts();
            onPublished?.();
        } catch (_e) {
            toast.error(de ? 'Veröffentlichen fehlgeschlagen' : 'Publish failed');
        } finally {
            setBusySlug(null);
        }
    };

    const discard = async (slug) => {
        setBusySlug(slug);
        try {
            await axios.delete(`${API}/blog/studio/drafts/${slug}`);
            toast.success(de ? 'Entwurf verworfen' : 'Draft discarded');
            loadDrafts();
        } catch (_e) {
            toast.error(de ? 'Fehler' : 'Error');
        } finally {
            setBusySlug(null);
        }
    };

    return (
        <Card className="border-2 border-dashed border-violet-300 bg-violet-50/40 mb-6" data-testid="blog-studio">
            <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                    <PenLine className="w-5 h-5 text-violet-600" />
                    <span className="text-sm font-bold">{de ? 'Nanouks Blog-Studio' : "Nanouk's blog studio"}</span>
                    {drafts.length > 0 && <Badge className="bg-violet-600">{drafts.length} {de ? 'Entwürfe' : 'drafts'}</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">
                    {de
                        ? 'Nanouk schreibt jede Woche automatisch einen Artikel-Entwurf. Du prüfst und gibst frei – erst dann wird er öffentlich.'
                        : 'Nanouk drafts an article every week. You review and approve – only then it goes public.'}
                </p>
                <div className="flex gap-2">
                    <Input
                        placeholder={de ? 'Optional: Thema vorgeben (z.B. Kreatin)' : 'Optional: suggest a topic'}
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        disabled={generating}
                        data-testid="blog-studio-topic-input"
                    />
                    <Button onClick={generate} disabled={generating} className="shrink-0 gap-1.5 bg-violet-600 hover:bg-violet-700" data-testid="blog-studio-generate-btn">
                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {de ? 'Generieren' : 'Generate'}
                    </Button>
                </div>

                {drafts.map((d) => (
                    <div key={d.slug} className="rounded-xl border border-violet-200 bg-white p-3 space-y-2" data-testid={`blog-draft-${d.slug}`}>
                        <button
                            onClick={() => setExpanded(expanded === d.slug ? null : d.slug)}
                            className="w-full flex items-start justify-between gap-2 text-left"
                            data-testid={`blog-draft-preview-${d.slug}`}
                        >
                            <div className="min-w-0">
                                <p className="text-sm font-semibold truncate">{d.title}</p>
                                <p className="text-xs text-muted-foreground line-clamp-2">{d.excerpt}</p>
                                <span className="text-[10px] text-violet-700 font-medium uppercase">{d.category} · {d.read_time} min</span>
                            </div>
                            <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-violet-600 border border-violet-200 rounded-full px-2 py-1">
                                {expanded === d.slug
                                    ? <>{de ? 'Zuklappen' : 'Collapse'} <ChevronUp className="w-3.5 h-3.5" /></>
                                    : <>{de ? 'Vorschau' : 'Preview'} <ChevronDown className="w-3.5 h-3.5" /></>}
                            </span>
                        </button>
                        {expanded === d.slug && (
                            <div className="text-xs whitespace-pre-wrap max-h-64 overflow-y-auto border-t pt-2 text-gray-700" data-testid="blog-draft-content">
                                {d.content}
                            </div>
                        )}
                        <div className="flex gap-2">
                            <Button size="sm" className="flex-1 gap-1 bg-green-600 hover:bg-green-700 h-8 text-xs" disabled={busySlug === d.slug} onClick={() => publish(d.slug)} data-testid={`blog-draft-publish-${d.slug}`}>
                                <Check className="w-3.5 h-3.5" /> {de ? 'Veröffentlichen' : 'Publish'}
                            </Button>
                            <Button size="sm" variant="outline" className="flex-1 gap-1 h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" disabled={busySlug === d.slug} onClick={() => discard(d.slug)} data-testid={`blog-draft-discard-${d.slug}`}>
                                <Trash2 className="w-3.5 h-3.5" /> {de ? 'Verwerfen' : 'Discard'}
                            </Button>
                        </div>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}
